type Primitive = string | number | boolean | null | undefined;
const isPrimitive = (value: unknown): value is Primitive => {
    return !!(
        typeof value === "string" ||
        typeof value === "number" ||
        typeof value === "boolean" ||
        value === null ||
        value === undefined
    );
};

type CreateErrorParserParams = {
    // Max level to unpack nested objects.
    maxLevel?: number;
};

/**
 * Creates an error parser that will handle circular references.
 * Should be created for each use to ensure a fresh WeakSet.
 * @returns unpackError function with built in handling for circular references
 */
export const createErrorParser = ({ maxLevel = 5 }: CreateErrorParserParams = {}) => {
    // Intialize a WeakSet to track seen objects. Used to detect circular references.
    const seen = new WeakSet<object>();

    const unpackValue = (value: unknown, level: number): unknown => {
        // Avoid deep nesting for permance reasons.
        if (level > maxLevel) {
            return value;
        }
        if (isPrimitive(value)) return value;

        if (value instanceof Error) {
            return {
                full: value.toString(),
                name: value.name,
                message: value.message,
                stack: value.stack,
                cause: "cause" in value ? (value as any).cause : undefined,
            };
        }

        // Arrays are returned as-is; we don't expect complex instances inside
        if (Array.isArray(value)) {
            return value;
        }

        if (typeof value === "object") {
            const obj = value as object;
            if (seen.has(obj)) return "[Circular]";
            seen.add(obj);

            const out: Record<string, unknown> = {};
            for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
                out[key] = unpackValue(v, level + 1);
            }
            return out;
        }

        return value;
    };

    // Public parser: requires an object input; values may be any nested type
    return <T extends Record<string, unknown>>(input: T): { [K in keyof T]: unknown } => {
        // Track the root to guard against direct self-reference
        seen.add(input as object);

        const out: Partial<{ [K in keyof T]: unknown }> = {};
        for (const [key, value] of Object.entries(input)) {
            (out as Record<string, unknown>)[key] = unpackValue(value, 1);
        }
        return out as { [K in keyof T]: unknown };
    };
};
